import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Profile from "../../../components/Organismos/Person/Profile";
import Offices from "../../../components/Organismos/Person/Offices";
import SocialMedia from "../../../components/Organismos/Person/SocialMedia";
import Bio from "../../../components/Organismos/Person/Bio";
import Education from "../../../components/Organismos/Person/Education";
import Languages from "../../../components/Organismos/Person/Languages";
import AOP from "../../../components/Organismos/Person/AOP";
import ProfessionalCareer from "../../../components/Organismos/Person/ProfessionalCareer";
import { usePersonForm } from "../../../components/Organismos/Person/personForm/PersonFormContext";
import { getOfficeByFirm } from "../../../Services/Office";
import { getAOPsByFirm } from "../../../Services/AreaOfPractice";
import { getLanguages } from "../../../Services/languge";
import { IOfficeNew } from "../../../interfaces/models/IOffice";
import type { IAreaOfPractice } from "../../../interfaces/models/IAreaOfPractice";
import { ILanguage } from "../../../interfaces";
import MemberProfileEditSection from "./MemberProfileEditSection";
import EditMyProfileActionBar from "./EditMyProfileActionBar";
import { ProfileEditorBrandAccentProvider } from "../../../Context/ProfileEditorBrandAccentContext";

export type EditMyProfileFormProps = {
  /** URL param used to reload the person after a successful save. */
  generatedId: string;
  /** Firm listing categories for title dropdown (from API). */
  listingCategory?: string[];
};

/**
 * Full-page editor: same sections and {@link usePersonForm} pipeline as Manage Firm → Professionals → Create,
 * with one save bar at the bottom.
 */
const EditMyProfileForm: React.FC<EditMyProfileFormProps> = ({
  generatedId,
  listingCategory,
}) => {
  const navigate = useNavigate();
  const { customPath } = useParams<{
    customPath: string;
    generatedId: string;
  }>();
  const { form, submit, submitting } = usePersonForm();

  const handleCancel = useCallback(() => {
    if (customPath && generatedId) {
      navigate(`/profile2/${customPath}/${generatedId}`);
    } else {
      navigate(-1);
    }
  }, [customPath, generatedId, navigate]);

  const [offices, setOffices] = useState<(IOfficeNew)[]>([]);
  const [firmAreasOfPractice, setFirmAreasOfPractice] = useState<IAreaOfPractice[]>([]);
  const [languages, setLanguages] = useState<ILanguage[]>([]);

  useEffect(() => {
    if (!form.firm?.trim()) {
      setOffices([]);
      setFirmAreasOfPractice([]);
      return;
    }
    let cancelled = false;
    const firmId = form.firm.trim();
    void Promise.all([getOfficeByFirm(firmId), getAOPsByFirm(firmId)]).then(
      ([officesData, aopsData]) => {
        if (cancelled) {
          return;
        }
        const o = (officesData ?? []) as (IOfficeNew)[];
        const a = Array.isArray(aopsData) ? aopsData : [];
        setOffices(o.sort((x, y) => (x.firmName ?? "").localeCompare(y.firmName ?? "")));
        setFirmAreasOfPractice(
          a.sort((x, y) =>
            x.displayName.localeCompare(y.displayName, undefined, {
              sensitivity: "base",
            }),
          ),
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [form.firm]);

  useEffect(() => {
    let cancelled = false;
    void getLanguages().then((list) => {
      if (!cancelled && Array.isArray(list)) {
        setLanguages(list);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const listingCategoryOverride = useMemo(
    () => listingCategory?.filter(Boolean),
    [listingCategory],
  );

  const handleSave = useCallback(async () => {
    try {
      const ok = await submit();
      if (!ok) {
        return;
      }
      navigate(-1);
    } catch {
      /* error toast from toastPromise */
    }
  }, [submit, navigate]);

  const showAop = Boolean(form.isAttorney);

  return (
    <ProfileEditorBrandAccentProvider>
      <div className="flex w-full min-w-0 flex-col overflow-x-hidden bg-gray-25">
        <div className="flex min-w-0 flex-col gap-4 text-start">
          <div className="w-full bg-white px-5 py-6 shadow-sm md:px-8 md:py-8">
            <Profile
              offices={offices}
              catalogMode={false}
              listingCategoryOverride={listingCategoryOverride}
              showResetPassword={false}
              shellClassName="flex w-full flex-col items-start gap-6 p-0"
            />
          </div>

          <MemberProfileEditSection
            title="Office Location"
            id="edit-profile-offices"
            titleClassName="!text-xl"
          >
            <Offices offices={offices} />
          </MemberProfileEditSection>

          <MemberProfileEditSection
            title="Social Media"
            id="edit-profile-social"
            titleClassName="!text-xl"
          >
            <SocialMedia />
          </MemberProfileEditSection>

          <MemberProfileEditSection
            title="Professional Bio"
            id="edit-profile-bio"
            titleClassName="!text-xl"
          >
            <Bio />
          </MemberProfileEditSection>

          <MemberProfileEditSection
            title="Education Information"
            id="edit-profile-education"
            titleClassName="!text-xl"
          >
            <Education />
          </MemberProfileEditSection>

          <MemberProfileEditSection
            title="Languages"
            id="edit-profile-languages"
            titleClassName="!text-xl"
          >
            <Languages
              languages={languages}
              onLanguageCreated={(lang) => {
                setLanguages((prev) => {
                  const without = prev.filter((l) => l.name !== lang.name);
                  return [...without, lang].sort((a, b) =>
                    a.name.localeCompare(b.name),
                  );
                });
              }}
            />
          </MemberProfileEditSection>

          {showAop ? (
            <MemberProfileEditSection
              title="Areas of Practice"
              id="edit-profile-aop"
              titleClassName="!text-xl"
            >
              <AOP firmAreasOfPractice={firmAreasOfPractice} />
            </MemberProfileEditSection>
          ) : null}

          <MemberProfileEditSection
            title="Professional Career"
            id="edit-profile-career"
            titleClassName="!text-xl"
          >
            <ProfessionalCareer />
          </MemberProfileEditSection>
        </div>

        <EditMyProfileActionBar
          onCancel={handleCancel}
          onSave={handleSave}
          disabled={submitting}
          saving={submitting}
        />
      </div>
    </ProfileEditorBrandAccentProvider>
  );
};

export default memo(EditMyProfileForm);
